/**
 * Utilities for calculating component condition degradation.
 */

/** Milliseconds per year (accounting for leap years) */
export const MS_PER_YEAR = 1000 * 60 * 60 * 24 * 365.25;

/**
 * Whether a condition has been assessed. 0 is a real assessment ("replace now",
 * selectable in iOS), so a condition is tested with this and never by truthiness.
 */
export const isConditionSet = (value: number | null | undefined): value is number =>
    typeof value === 'number' && !isNaN(value);

/**
 * Calculate the current (degraded) condition of a component based on time elapsed since assessment.
 *
 * The condition degrades linearly over the expected lifetime:
 * - A condition of 10 means the component is new (full lifetime remaining)
 * - A condition of 0 means the component needs replacement
 * - Degradation rate = 10 / expectedLifetimeYears per year
 *
 * @param assessedCondition - The condition value when last assessed (0-10), or null/undefined
 * @param assessmentDate - When the assessment was made
 * @param expectedLifetimeYears - Expected lifetime of the component in years
 * @returns Current degraded condition value (0-10), or null if inputs are invalid
 *
 * @example
 * const current = calculateCurrentCondition(8.0, '2023-01-15', 30);
 * // If called in 2025: returns ~7.33 (degraded 0.67 over 2 years)
 */
export const calculateCurrentCondition = (
    assessedCondition: number | null | undefined,
    assessmentDate: Date | string,
    expectedLifetimeYears: number
): number | null => {
    if (assessedCondition == null || expectedLifetimeYears <= 0) {
        return null;
    }

    const assessDate = typeof assessmentDate === 'string' ? new Date(assessmentDate) : assessmentDate;
    const now = new Date();

    // Calculate years elapsed since assessment
    const yearsElapsed = (now.getTime() - assessDate.getTime()) / MS_PER_YEAR;

    // Calculate degradation rate per year (condition goes from 10 to 0 over lifetime)
    const degradationPerYear = 10 / expectedLifetimeYears;

    // Calculate current condition
    const currentCondition = assessedCondition - (yearsElapsed * degradationPerYear);

    // Clamp to valid range [0, 10]
    return Math.max(0, Math.min(10, currentCondition));
};
